import { useCalcNavbarHeight } from '@/hooks/useCalcNavbarHeight';
import { useMeasure } from '@/hooks/useMeasure';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { gaReport } from '@/shared/util/analytics';
import { measureScreenSize } from '@/shared/util/measureScreenSize';
import { useNavigation } from '@remix-run/react';
import { usePersistFn } from 'ahooks';
import clsx from 'clsx';
import React from 'react';
import { ProgressBar } from '../progress-bar/ProgressBar';
import { Background } from './components/background/Background';
import { DebugPanelGroup } from './components/debug-panel-group/DebugPanelGroup';
import { NavbarContent } from './components/navbar-content/NavbarContent';
import { ResourcePanel } from './components/resource-panel/ResourcePanel';
import { TopNotification } from './components/top-notification/TopNotification';

export function DocsLayout() {
  const navbarHeight = useCalcNavbarHeight();
  const navbarHeightRef = React.useRef(navbarHeight);
  navbarHeightRef.current = navbarHeight;
  const setScreenSize = useAppearanceConfigModelStore((x) => x.setScreenSize);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const [ref] = useMeasure((x) => {
    const screenSize = measureScreenSize(x.width);
    setScreenSize(screenSize);
  });
  const [isScrollTop, setIsScrollTop] = React.useState(true);
  const onScroll = usePersistFn((e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const newIsScrollTop = e.currentTarget.scrollTop <= 50;
    if (newIsScrollTop !== isScrollTop) {
      setIsScrollTop(newIsScrollTop);
    }
  });
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const navigation = useNavigation();
  React.useEffect(() => {
    let timer = -1;
    const hash = decodeURIComponent(window.location.hash.substring(1));
    if (scrollContainerRef.current && navigation.state === 'idle') {
      if (!hash) {
        scrollContainerRef.current.scrollTo({ top: 0 });
      } else {
        timer = window.setTimeout(() => {
          const hashElement = document.getElementById(hash);
          if (hashElement) {
            const hashElementOffsetTop = (() => {
              const result = (hashElement?.offsetTop ?? 0) - navbarHeightRef.current;
              if (result < 0) {
                return 0;
              }
              return result;
            })();

            scrollContainerRef.current?.scrollTo({ top: hashElementOffsetTop });
          }
        }, 0);
      }
    }

    return () => {
      window.clearTimeout(timer);
    };
  }, [navigation.state]);

  React.useEffect(() => {
    gaReport(DOCS_SITE_EVENT.PAGE_VIEW, { page_title: title });
  }, []);

  return (
    <div className="_docs-layout flex h-full w-full flex-col" ref={ref}>
      <ProgressBar />
      <TopNotification />

      <div className="_container relative h-0 w-full flex-1">
        <div
          className="relative h-full w-full overflow-y-auto"
          id="main-scroll-container"
          onScroll={onScroll}
          ref={scrollContainerRef}
        >
          <Background />
          <NavbarContent
            className={clsx('backdrop-blur-lg', {
              ['bg-[#ffffff7a] dark:bg-transparent']: isScrollTop,
              ['bg-[#fffffff2] dark:bg-[#191a23f2]']: !isScrollTop,
            })}
          />

          <ResourcePanel />
        </div>

        <DebugPanelGroup />
      </div>
    </div>
  );
}
